import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faFileAlt,
  faPaperPlane,
  faExclamationCircle,
  faCheckCircle,
  faSpinner,
  faUserAlt,
  faEnvelope,
  faPhone
} from '@fortawesome/free-solid-svg-icons';
import styles from './ServiceRequestModal.module.scss';

const ServiceRequestModal = ({ isOpen, onClose, service, onSubmit, user }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    guest_name: '',
    guest_phone: '',
    guest_email: ''
  });

  const isGuest = !user;

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  if (!isOpen || !service) return null;

  const priorityOptions = [
    { value: 'low', label: 'Низкий', description: 'Не срочно, можно выполнить позже', color: '#4CAF50' },
    { value: 'medium', label: 'Средний', description: 'Обычная заявка', color: '#2196F3' },
    { value: 'high', label: 'Высокий', description: 'Требуется в ближайшее время', color: '#FF9800' },
    { value: 'urgent', label: 'Критический', description: 'Срочно, максимальный приоритет', color: '#F44336' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Пожалуйста, укажите название заявки';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Пожалуйста, опишите вашу задачу';
    } else if (formData.description.trim().length < 20) {
      newErrors.description = 'Описание должно содержать минимум 20 символов';
    }

    if (isGuest) {
      if (!formData.guest_name.trim()) {
        newErrors.guest_name = 'Укажите ваше имя';
      }
      if (!formData.guest_phone.trim()) {
        newErrors.guest_phone = 'Укажите телефон для связи';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        priority: formData.priority
      };
      if (isGuest) {
        payload.guest_name = formData.guest_name;
        payload.guest_phone = formData.guest_phone;
        payload.guest_email = formData.guest_email;
      }
      await onSubmit(payload);

      setSubmitSuccess(true);
      
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      setErrors({
        submit: error.response?.data?.message || 'Ошибка при отправке заявки. Попробуйте позже.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      guest_name: '',
      guest_phone: '',
      guest_email: ''
    });
    setErrors({});
    setSubmitSuccess(false);
    setIsSubmitting(false);
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      handleClose();
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick} data-lenis-prevent>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={handleClose} disabled={isSubmitting}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        {submitSuccess ? (
          <div className={styles.successMessage}>
            <div className={styles.successIcon}>
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <h3>Заявка успешно отправлена!</h3>
            <p>Мы свяжемся с вами в ближайшее время для уточнения деталей.</p>
          </div>
        ) : (
          <>
            <div className={styles.modalHeader}>
              <div className={styles.serviceInfo}>
                <h2>Оставить заявку</h2>
                <div className={styles.serviceName}>
                  <FontAwesomeIcon icon={faFileAlt} />
                  <span>{service.name}</span>
                </div>
                {service.price_from && service.price_from > 0 && (
                  <div className={styles.servicePrice}>
                    Стоимость: <strong>от {new Intl.NumberFormat('ru-RU').format(service.price_from)} сом</strong>
                  </div>
                )}
              </div>
            </div>

            <form className={styles.modalForm} onSubmit={handleSubmit}>
              {errors.submit && (
                <div className={styles.errorAlert}>
                  <FontAwesomeIcon icon={faExclamationCircle} />
                  <span>{errors.submit}</span>
                </div>
              )}

              <div className={styles.formGroup}>
                <label htmlFor="title">
                  Название заявки <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Например: Разработка корпоративного сайта"
                  className={errors.title ? styles.error : ''}
                  disabled={isSubmitting}
                />
                {errors.title && (
                  <span className={styles.errorMessage}>
                    <FontAwesomeIcon icon={faExclamationCircle} />
                    {errors.title}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="description">
                  Описание задачи <span className={styles.required}>*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Опишите подробно, что вам требуется. Укажите желаемые сроки, особые требования..."
                  rows="3"
                  className={errors.description ? styles.error : ''}
                  disabled={isSubmitting}
                />
                <div className={styles.charCount}>
                  {formData.description.length} символов (минимум 20)
                </div>
                {errors.description && (
                  <span className={styles.errorMessage}>
                    <FontAwesomeIcon icon={faExclamationCircle} />
                    {errors.description}
                  </span>
                )}
              </div>

              {isGuest && (
                <>
                  <div className={styles.formGroup}>
                    <label htmlFor="guest_name">
                      Ваше имя <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      id="guest_name"
                      name="guest_name"
                      value={formData.guest_name}
                      onChange={handleChange}
                      placeholder="Как к вам обращаться?"
                      className={errors.guest_name ? styles.error : ''}
                      disabled={isSubmitting}
                    />
                    {errors.guest_name && (
                      <span className={styles.errorMessage}>
                        <FontAwesomeIcon icon={faExclamationCircle} />
                        {errors.guest_name}
                      </span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="guest_phone">
                      Телефон <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="tel"
                      id="guest_phone"
                      name="guest_phone"
                      value={formData.guest_phone}
                      onChange={handleChange}
                      placeholder="+996 700 000 000"
                      className={errors.guest_phone ? styles.error : ''}
                      disabled={isSubmitting}
                    />
                    {errors.guest_phone && (
                      <span className={styles.errorMessage}>
                        <FontAwesomeIcon icon={faExclamationCircle} />
                        {errors.guest_phone}
                      </span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="guest_email">Email</label>
                    <input
                      type="email"
                      id="guest_email"
                      name="guest_email"
                      value={formData.guest_email}
                      onChange={handleChange}
                      placeholder="email@example.com"
                      disabled={isSubmitting}
                    />
                  </div>
                </>
              )}

              <div className={styles.formGroup}>
                <label htmlFor="priority">
                  Приоритет <span className={styles.required}>*</span>
                </label>
                <div className={styles.priorityOptions}>
                  {priorityOptions.map(option => (
                    <label
                      key={option.value}
                      className={`${styles.priorityOption} ${formData.priority === option.value ? styles.selected : ''}`}
                      style={{
                        '--priority-color': option.color
                      }}
                    >
                      <input
                        type="radio"
                        name="priority"
                        value={option.value}
                        checked={formData.priority === option.value}
                        onChange={handleChange}
                        disabled={isSubmitting}
                      />
                      <div className={styles.optionContent}>
                        <span className={styles.optionLabel}>{option.label}</span>
                        <span className={styles.optionDescription}>{option.description}</span>
                      </div>
                      <div className={styles.optionIndicator}></div>
                    </label>
                  ))}
                </div>
              </div>

              <div className={styles.formActions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      Отправка...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPaperPlane} />
                      Отправить заявку
                    </>
                  )}
                </button>
              </div>

              <div className={styles.formNote}>
                <FontAwesomeIcon icon={faExclamationCircle} />
                <p>
                  После отправки заявки наш менеджер свяжется с вами для уточнения деталей и обсуждения сроков выполнения.
                </p>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceRequestModal;
